import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Trash2, Plus, FileSpreadsheet } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Modal } from '@/shared/components/ui/Modal';
import { getApiErrorMessage } from '@/shared/api/axios';
import { usePunches, usePunchActions, useDevices, type PunchFilters } from '../hooks/useBiometricAdmin';
import { PUNCH_STATES, VERIFY_MODES, istDateTime } from '../biometricTypes';
import { PunchImportModal } from './PunchImportModal';
import { PunchPhoto } from './PunchPhoto';

const STATE_TONE: Record<number, 'success' | 'info' | 'warning' | 'neutral'> = {
  0: 'success',
  1: 'info',
  2: 'warning',
  3: 'warning',
  4: 'neutral',
  5: 'neutral',
};

export function PunchLogTable() {
  const { data: devices } = useDevices(0);
  const [filters, setFilters] = useState<PunchFilters>({ page: 1, limit: 100 });
  const { data, isLoading } = usePunches(filters);
  const { addManual, remove } = usePunchActions();
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [manual, setManual] = useState({ biometric_id: '', punch_time: '', punch_state: '0', device_sn: '' });

  const set = (patch: Partial<PunchFilters>) => setFilters(prev => ({ ...prev, ...patch, page: 1 }));

  const submitManual = async () => {
    if (!manual.biometric_id || !manual.punch_time) {
      toast.error('PIN and time are both required.');
      return;
    }
    try {
      await addManual.mutateAsync({
        biometric_id: manual.biometric_id,
        // datetime-local has no zone; the punch is IST like every device clock.
        punch_time: `${manual.punch_time}:00+05:30`,
        punch_state: Number(manual.punch_state),
        device_sn: manual.device_sn,
      });
      toast.success('Manual punch recorded and the day recomputed.');
      setAdding(false);
      setManual({ biometric_id: '', punch_time: '', punch_state: '0', device_sn: '' });
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <Card className="space-y-4 p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-on-surface">Punch log</h2>
          <p className="text-sm text-on-surface-variant">
            Raw reads as the device sent them. Deleting is a soft delete and is audited.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setImporting(true)}>
            <FileSpreadsheet className="mr-1 h-4 w-4" />
            Import from Excel
          </Button>
          <Button onClick={() => setAdding(true)}>
            <Plus className="mr-1 h-4 w-4" />
            Add manual punch
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input
          className="w-32"
          placeholder="PIN"
          value={filters.pin ?? ''}
          onChange={e => set({ pin: e.target.value })}
        />
        <Input type="date" className="w-40" value={filters.from ?? ''} onChange={e => set({ from: e.target.value })} />
        <Input type="date" className="w-40" value={filters.to ?? ''} onChange={e => set({ to: e.target.value })} />
        <select
          className="h-10 rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
          value={filters.sn ?? ''}
          onChange={e => set({ sn: e.target.value })}
        >
          <option value="">All devices</option>
          {(devices ?? []).map(d => (
            <option key={d.serial_number} value={d.serial_number}>
              {d.name || d.serial_number}
            </option>
          ))}
        </select>
        <select
          className="h-10 rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
          value={filters.state ?? ''}
          onChange={e => set({ state: e.target.value })}
        >
          <option value="">Any state</option>
          {Object.entries(PUNCH_STATES).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          className="h-10 rounded-md border border-outline-variant bg-surface px-2 text-sm text-on-surface"
          value={filters.verify ?? ''}
          onChange={e => set({ verify: e.target.value })}
        >
          <option value="">Any verify mode</option>
          {Object.entries(VERIFY_MODES).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-12"><Spinner /></div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead>
                <tr className="border-b border-outline-variant text-left text-xs uppercase tracking-wide text-on-surface-variant">
                  <th className="px-2 py-2">Time (IST)</th>
                  <th className="px-2 py-2">PIN</th>
                  <th className="px-2 py-2">Employee</th>
                  <th className="px-2 py-2">Device</th>
                  <th className="px-2 py-2">State</th>
                  <th className="px-2 py-2">Verify</th>
                  <th className="px-2 py-2">Photo</th>
                  <th className="px-2 py-2 text-right">—</th>
                </tr>
              </thead>
              <tbody>
                {(data?.punches ?? []).map(punch => (
                  <tr key={punch._id} className="border-b border-outline-variant/50 hover:bg-surface-variant/30">
                    <td className="whitespace-nowrap px-2 py-1.5 font-mono text-xs">{istDateTime(punch.punch_time)}</td>
                    <td className="px-2 py-1.5 font-mono">{punch.biometric_id}</td>
                    <td className="px-2 py-1.5">
                      {punch.user_id?.display_name ?? (
                        <span className="text-xs text-warning">unmapped</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs text-on-surface-variant">
                      {punch.device_sn || punch.device_code || '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      <Badge tone={STATE_TONE[punch.punch_state ?? -1] ?? 'neutral'}>
                        {PUNCH_STATES[punch.punch_state ?? -1] ?? punch.punch_state}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5 text-xs text-on-surface-variant">
                      {VERIFY_MODES[punch.verification_mode ?? 1] ?? punch.verification_mode}
                    </td>
                    <td className="px-2 py-1.5">
                      {punch.att_photo_path ? (
                        <PunchPhoto path={punch.att_photo_path} />
                      ) : (
                        <span className="text-xs text-on-surface-variant">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        type="button"
                        title="Soft-delete this punch"
                        className="rounded p-1 text-danger hover:bg-danger/10"
                        onClick={async () => {
                          if (!window.confirm('Delete this punch? The day will be recomputed.')) return;
                          try {
                            await remove.mutateAsync(punch._id);
                            toast.success('Punch removed.');
                          } catch (err) {
                            toast.error(getApiErrorMessage(err));
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data && data.total === 0 && (
            <p className="py-8 text-center text-sm text-on-surface-variant">No punches match these filters.</p>
          )}

          {data && data.pages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-on-surface-variant">
                Page {data.page} of {data.pages} · {data.total} punches
              </span>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={data.page <= 1}
                  onClick={() => setFilters(p => ({ ...p, page: (p.page ?? 1) - 1 }))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={data.page >= data.pages}
                  onClick={() => setFilters(p => ({ ...p, page: (p.page ?? 1) + 1 }))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {importing && <PunchImportModal onClose={() => setImporting(false)} />}

      <Modal open={adding} onClose={() => setAdding(false)} title="Add a manual punch">
        <div className="space-y-3">
          <p className="text-sm text-on-surface-variant">
            Use this when a device was down. The entry is marked <code>manual</code> and audited.
          </p>
          <Input
            placeholder="PIN (enroll number)"
            value={manual.biometric_id}
            onChange={e => setManual(m => ({ ...m, biometric_id: e.target.value }))}
          />
          <Input
            type="datetime-local"
            value={manual.punch_time}
            onChange={e => setManual(m => ({ ...m, punch_time: e.target.value }))}
          />
          <select
            className="h-10 w-full rounded-md border border-outline-variant bg-surface px-3 text-sm text-on-surface"
            value={manual.punch_state}
            onChange={e => setManual(m => ({ ...m, punch_state: e.target.value }))}
          >
            {Object.entries(PUNCH_STATES).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAdding(false)}>Cancel</Button>
            <Button onClick={submitManual} disabled={addManual.isPending}>Record punch</Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
